import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useRevalidator, Outlet, useNavigate, useLocation, useNavigation, Link, useLoaderData, useOutletContext } from 'react-router';
import { PlusCircleIcon } from '@heroicons/react/16/solid';
import ProjectTable from './ProjectTable';
import MapComponent from '../features/map/components/MapComponent';
import LoadingScreen from "./LoadingScreen";
import RegistrationPage from '../features/system_registration/components/RegistrationPage';
import { siteToMarker } from '../utils';
import { MapMarker } from '../features/map/types';
import { ProjectListWithGenerationSummary, Site, StatusFilter, User } from '../types';
import ProjectSummary from './ProjectSummary';
import { getProjectsWithGenerationSummary } from '../api';
import InstallerInvitationPage from '../features/system_registration/components/InstallerInvitationPage';
import InvitationSendFormPage from '../features/system_registration/components/InvitationSendFormPage';
import BackendErrorPage from './BackendErrorPage';

interface DashboardOutletContext {
    user: User | null;
    hoveredSiteId: number | null;
    setHoveredSiteId: (id: number | null) => void;
    isLoading: boolean;
    markers: MapMarker[];
    setMarkers: Dispatch<SetStateAction<MapMarker[]>>;
    statusFilter: StatusFilter;
    setStatusFilter: Dispatch<SetStateAction<StatusFilter>>;
    currentSiteId: number | null;
    setCurrentSiteId: Dispatch<SetStateAction<number | null>>;
    navigationMarkers: MapMarker[] | null;
    setNavigationMarkers: Dispatch<SetStateAction<MapMarker[] | null>>;
}

const Dashboard = () => {
    const { user } = useOutletContext<{ user: User | null }>();
    const [markers, setMarkers] = useState<MapMarker[]>([]);
    const revalidator = useRevalidator();
    const [hoveredSiteId, setHoveredSiteId] = useState<number | null>(null);
    const [statusFilter, setStatusFilter] = useState<StatusFilter>({
        'normal': true,
        'system-fault': true,
        'no-data': true,
        'under-construction': true,
        'manual': true,
        'not-on-map': true,
    });
    const navigate = useNavigate();
    const location = useLocation();
    const isDashboardView = location.pathname === '/dashboard' || location.pathname === '/dashboard/';
    const isInvitationSendFormView = location.pathname.includes('/invite');
    const isInstallerInvitationView = location.pathname.includes('/installers');
    const isRegistrationView = location.pathname.includes('/register');
    const isSiteView = location.pathname.includes('/dashboard/site/');
    const [error, setError] = useState<string | null>(null);
    const [projectsSummary, setProjectsSummary] = useState<ProjectListWithGenerationSummary | null>(null);
    const [dataLoading, setDataLoading] = useState(false);
    const navigation = useNavigation();
    const isLoading = navigation.state === 'loading';
    const [currentSiteId, setCurrentSiteId] = useState<number | null>(null);
    const [navigationMarkers, setNavigationMarkers] = useState<MapMarker[] | null>(null);

    useEffect(() => {
        setHoveredSiteId(null);
        if (isLoading) {
            setDataLoading(true);
        } else {
            setDataLoading(false);
        }
    }, [isLoading]);

    useEffect(() => {
        if (!isSiteView) {
            setCurrentSiteId(null);
            setNavigationMarkers(null);
        }
    }, [isSiteView]);

    // Reset filter when navigating back to dashboard
    useEffect(() => {
        if (isDashboardView) {
            setStatusFilter({
                'normal': true,
                'system-fault': true,
                'no-data': true,
                'under-construction': true,
                'manual': true,
                'not-on-map': true,
            });
        }
    }, [isDashboardView]);

    const context: DashboardOutletContext = {
        user,
        hoveredSiteId,
        setHoveredSiteId,
        isLoading,
        markers,
        setMarkers,
        statusFilter,
        setStatusFilter,
        currentSiteId,
        setCurrentSiteId,
        navigationMarkers,
        setNavigationMarkers
    };

    const filterSites = (sites: Site[]) => {
        return sites.filter(site => {
            const hasCoordinates = site.latitude && site.longitude;
            if (!hasCoordinates) {
                return statusFilter['not-on-map'];
            }
            const status = site.health_status as keyof StatusFilter;
            if (status in statusFilter) {
                return statusFilter[status];
            }
            return true;
        });
    };

    useEffect(() => {
        if (isDashboardView) {
            if (projectsSummary) {
                const allSites = projectsSummary.instances.flatMap(project => project.sites ?? []);
                const filteredSites = filterSites(allSites);
                setMarkers(filteredSites.map(site => siteToMarker(site)));
            } else {
                setDataLoading(true);
                setMarkers([]);
                setError(null);
                getProjectsWithGenerationSummary()
                .then(summary => {
                    setProjectsSummary(summary);
                    const allSites = summary.instances.flatMap(project => project.sites ?? []);
                    const filteredSites = filterSites(allSites);
                    setMarkers(filteredSites.map(site => siteToMarker(site)));
                })
                .catch((err: Error) => {
                    setError(err.message);
                })
                .finally(() =>  setDataLoading(false));
            }
        }
    }, [location.pathname, statusFilter]);

    const refreshData = () => {
        revalidator.revalidate();
    };

    const navigateToSite = (siteId: number) => {
        navigate(`/dashboard/site/${siteId}`);
    };

    return (
        <div className='flex grow gap-8 m-2 flex-col lg:flex-row pt-2 overflow-hidden'>
            {isRegistrationView
                ?  <div className='h-full w-1/2 rounded-lg overflow-y-auto'> <RegistrationPage /> </div>
                : isInvitationSendFormView
                    ? <div className='h-full w-1/2 rounded-lg overflow-y-auto'> <InvitationSendFormPage /> </div>
                    : isInstallerInvitationView
                        ? <div className='h-full w-1/2 rounded-lg overflow-y-auto'> <InstallerInvitationPage /> </div>
                        : <div className='h-1/2 lg:w-1/2 lg:h-full rounded-lg overflow-hidden relative z-0'>
                            <MapComponent
                                isLoading={dataLoading || isLoading}
                                markers={markers}
                                onMarkerClick={navigateToSite}
                                onMarkerHover={setHoveredSiteId}
                                hoveredMarkerId={hoveredSiteId}
                                statusFilter={statusFilter}
                                onStatusFilterChange={setStatusFilter}
                                showNavigation={isSiteView}
                                currentSiteId={currentSiteId}
                                onNavigateToSite={navigateToSite}
                                navigationMarkers={navigationMarkers ?? undefined}
                            />
                        </div>
            }
            <div className='h-1/2 lg:w-1/2 lg:h-full overflow-y-auto relative z-10'>
                {error ? (
                    <BackendErrorPage error={error} />
                ) : isDashboardView && !isLoading ? (
                    <>
                        <ProjectSummary projectsSummary={projectsSummary || { instances: [], results: {} }} />
                        <div className="relative">
                            <ProjectTable
                                projects={projectsSummary ? projectsSummary.instances : []}
                                error={null}
                                refreshData={refreshData}
                                hoveredSiteId={hoveredSiteId}
                                onRowHover={setHoveredSiteId}
                                onSiteClick={navigateToSite}
                            />
                            <div className="flex justify-end m-2">
                                <Link
                                    to="/dashboard/project/new"
                                    className="flex gap-1 p-2 items-center text-sm text-gray-500 hover:text-gray-600"
                                >
                                    <PlusCircleIcon className="w-5 h-5" />
                                    Project
                                </Link>
                            </div>
                        </div>
                    </>
                ) :
                    isLoading
                        ? <LoadingScreen />
                        : <div>
                            <Outlet context={context} />
                        </div>
                }
            </div>
        </div>
    );
};

export default Dashboard;
export type { DashboardOutletContext };
